import { useState, useRef } from 'react';
import { ToastData } from '@/Shared';

/**
 * ToastHook interface – exposes the toast state and control methods.
 * 
 * Responsibilities:
 * - Tracks current toast data (`toastData`)
 * - Provides methods to open and close a toast
 * - Ensures `onClose` callback is fired only when toast is explicitly closed
 */
export interface ToastHook {
    /** Current toast data, null if no toast is displayed */
    toastData?: ToastData | null,

    /** Opens a toast with given data and optional onClose callback */
    openToast: (params: { data: ToastData, onClose?: () => void }) => void,

    /** Closes the current toast and triggers the stored onClose callback */
    closeToast: () => void,
}


/**
 * useToast hook – manages toast notifications for the app.
 * 
 * Author: Samano CASTRE  
 * Date: January 29, 2026
 * 
 * Behavior:
 * - `openToast` sets the current toast data and stores an optional onClose callback
 * - `closeToast` clears the toast data and calls the stored callback
 * - `onCloseRef` ensures the callback is only fired when closing the toast
 */
export function useToast(): ToastHook {

    /** State for the currently displayed toast */
    const [toastData, setToastData] = useState<ToastData | null>(null);

    /** Ref to hold the onClose callback to avoid re-triggering on each render */
    const onCloseRef = useRef<(() => void) | undefined>();

    /** Opens a toast with data and optional onClose callback */
    const openToast = (params: { data: ToastData, onClose?: () => void }) => {
        setToastData(params.data);
        onCloseRef.current = params.onClose;
    }

    /** Closes the current toast and fires the stored onClose callback */
    const closeToast = () => {
        setToastData(null);
        onCloseRef.current?.();
        onCloseRef.current = undefined; // reset to prevent double-calls
    }

    return {
        toastData,
        openToast,
        closeToast,
    };
}
